import { BasketActions, SearchActions } from "@/sagas/actions";
import { useEffect, useRef, useState } from "react";
import { Star, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function Favorites() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const favorites = useSelector((state: any) => state.basket.favorites);
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    // Close on outside click / Escape — the panel lives in the fixed header.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        window.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => {
        if (favorites.length === 0) setOpen(false);
    }, [favorites]);

    const handleRemoveFavorite = (item: any) => {
        dispatch({ type: BasketActions.REMOVE_FROM_FAVORITES, payload: item });
    };

    const handleNavItem = (item: any) => {
        setOpen(false);
        dispatch({ type: SearchActions.FETCH_DETAILS, payload: { identifier: item.identifier } });
        dispatch({ type: SearchActions.SET_SELECTED, payload: item });
        navigate('/item/' + item['identifier']);
    };

    return (
        <div className="relative flex" ref={rootRef}>
            <button
                type="button"
                onClick={() => favorites.length > 0 && setOpen((v) => !v)}
                title={favorites.length > 0 ? `${favorites.length} favourite record(s)` : 'No favourites yet'}
                aria-label="Favourites"
                aria-expanded={open}
                className="relative flex h-8 w-8 items-center justify-center rounded-md border border-white/20 text-white/80 transition-colors duration-150 hover:bg-white/10 hover:text-white disabled:opacity-60"
                disabled={favorites.length === 0}
            >
                <Star className="h-4 w-4" fill={favorites.length > 0 ? 'currentColor' : 'none'} />
                {favorites.length > 0 && (
                    <span className="nz-mono absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-[3px] bg-warm-fill px-1 text-[10px] font-bold text-[#00195a]">
                        {favorites.length}
                    </span>
                )}
            </button>

            {open && favorites.length > 0 && (
                <div className="nz-card nz-scroll absolute right-0 top-[calc(100%+10px)] z-[70] max-h-[min(60vh,420px)] w-[320px] overflow-y-auto p-2 shadow-float animate-[fadeup_.16s_ease_both]">
                    <div className="flex items-center justify-between px-2 pb-2">
                        <span className="nz-label">Favourites</span>
                        <button type="button" className="nz-icon-btn h-6 w-6" onClick={() => setOpen(false)} aria-label="Close">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <div className="flex flex-col gap-1">
                        {favorites.map((item: any, key: number) => (
                            <div
                                key={key}
                                className="group flex items-start gap-2 rounded-md border border-line-2 p-2 transition-colors hover:border-brand-line hover:bg-surface-2"
                            >
                                <Star className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warm-fill" fill="currentColor" />
                                <button
                                    type="button"
                                    onClick={() => handleNavItem(item)}
                                    className="min-w-0 flex-1 text-left"
                                >
                                    <span className="block break-words text-[13px] font-medium leading-snug text-ink">
                                        {item.title || item.identifier}
                                    </span>
                                    {item.title && (
                                        <span className="nz-mono block truncate text-[11px] text-ink-3">{item.identifier}</span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="nz-icon-btn h-6 w-6 shrink-0"
                                    onClick={() => handleRemoveFavorite(item)}
                                    aria-label="Remove from favourites"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
